import DOMPurify from 'dompurify';

import {
  decodeEquationLatex,
  normalizeBookEquation,
} from './bookEquationCodec';


const CELL_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'sub',
  'sup',
  'span',
  'div',
  'a',
  'ul',
  'ol',
  'li',
];

const CELL_ATTRIBUTES = [
  'style',
  'href',
  'target',
  'rel',
  'data-book-equation',
  'data-equation-display',
  'data-equation-latex',
];

const FONT_FAMILIES = new Set([
  'Arial, sans-serif',
  'Times New Roman, serif',
  'Calibri, sans-serif',
  'Verdana, sans-serif',
  'Georgia, serif',
  "'Courier New', Courier, monospace",
  'Courier New, Courier, monospace',
  'Impact, Haettenschweiler, Arial Black, sans-serif',
  "'Comic Sans MS', 'Comic Sans', cursive",
  'Comic Sans MS, Comic Sans, cursive',
]);

const FONT_SIZES = new Set([12, 14, 16, 18, 20, 24, 28, 32]);
const BLOCK_TAGS = ['p', 'div', 'li'];

const normalizeStyle = (value) => {
  const safe = [];
  String(value || '')
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .forEach((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator === -1) return;
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const rawValue = declaration.slice(separator + 1).trim();
      if (property === 'color') {
        let color = /^#[0-9a-f]{6}$/i.test(rawValue)
          ? rawValue.toUpperCase()
          : null;
        const rgb = rawValue.match(
          /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i
        );
        if (rgb) {
          const channels = rgb.slice(1).map(Number);
          if (channels.every((channel) => channel >= 0 && channel <= 255)) {
            color = `#${channels
              .map((channel) => channel.toString(16).padStart(2, '0'))
              .join('')}`.toUpperCase();
          }
        }
        if (color) safe.push(`color: ${color}`);
      }
      if (property === 'font-family' && FONT_FAMILIES.has(rawValue)) {
        safe.push(`font-family: ${rawValue}`);
      }
      if (property === 'font-size') {
        const match = rawValue.match(/^(\d+)px$/i);
        if (match && FONT_SIZES.has(Number(match[1]))) {
          safe.push(`font-size: ${Number(match[1])}px`);
        }
      }
    });
  return safe.join('; ');
};

const normalizeEquationAnchors = (root) => {
  root
    .querySelectorAll('[data-book-equation], [data-equation-display], [data-equation-latex]')
    .forEach((element) => {
      const display = element.getAttribute('data-equation-display');
      const encoded = element.getAttribute('data-equation-latex');
      const expectedTag = display === 'inline' ? 'SPAN' : 'DIV';
      try {
        const latex = decodeEquationLatex(encoded);
        const equation = normalizeBookEquation({ latex, display });
        if (
          element.tagName !== expectedTag ||
          element.getAttribute('data-book-equation') !== 'true' ||
          element.childNodes.length !== 0 ||
          equation.encoded !== encoded
        ) {
          throw new Error('invalid anchor');
        }
        [...element.attributes].forEach((attribute) => {
          if (!CELL_ATTRIBUTES.includes(attribute.name)) {
            element.removeAttribute(attribute.name);
          }
        });
      } catch {
        element.remove();
      }
    });
};

export const sanitizeTableCellHtml = (html) => {
  if (html === null || html === undefined || html === '') return '';
  const sanitized = DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS: CELL_TAGS,
    ALLOWED_ATTR: CELL_ATTRIBUTES,
    ALLOW_DATA_ATTR: true,
  });
  const root = document.createElement('div');
  root.innerHTML = sanitized;

  root.querySelectorAll('[style]').forEach((element) => {
    const style = normalizeStyle(element.getAttribute('style'));
    if (style) element.setAttribute('style', style);
    else element.removeAttribute('style');
  });
  root.querySelectorAll('a').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href) {
      link.removeAttribute('target');
      link.removeAttribute('rel');
      return;
    }
    if (link.getAttribute('target') === '_blank') {
      link.setAttribute('rel', 'noopener noreferrer');
    } else {
      link.removeAttribute('target');
      link.removeAttribute('rel');
    }
  });
  normalizeEquationAnchors(root);
  return root.innerHTML;
};

export const tableCellHtmlToPlainText = (html) => {
  const root = document.createElement('div');
  root.innerHTML = sanitizeTableCellHtml(html);
  root.querySelectorAll('[data-book-equation="true"]').forEach((element) => {
    try {
      element.replaceWith(
        document.createTextNode(
          decodeEquationLatex(element.getAttribute('data-equation-latex'))
        )
      );
    } catch {
      element.remove();
    }
  });
  root.querySelectorAll(BLOCK_TAGS.join(',')).forEach((element) => {
    element.after(document.createTextNode(' '));
  });
  return root.textContent.replace(/\s+/g, ' ').trim();
};

export const tableCellTextToEditorHtml = (text) => {
  const paragraph = document.createElement('p');
  paragraph.textContent = String(text ?? '');
  return paragraph.outerHTML;
};
